/**
 * Network Status Service
 *
 * Provides real-time network connectivity monitoring:
 * - Online/offline detection
 * - Connection quality assessment
 * - Automatic retry queue for failed requests
 * - Event-based status updates
 *
 * Copyright Deskshare, Inc.
 * All rights reserved.
 */

import { logger } from "@/lib/logger";

type ConnectionQuality = "excellent" | "good" | "poor" | "offline";

interface NetworkStatus {
  isOnline: boolean;
  quality: ConnectionQuality;
  lastCheckedAt: number;
  rtt: number | null; // Round-trip time in ms
  downlink: number | null; // Mbps
}

type NetworkStatusListener = (status: NetworkStatus) => void;

interface PendingRequest {
  id: string;
  executor: () => Promise<unknown>;
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
  createdAt: number;
  retryCount: number;
  maxRetries: number;
}

class NetworkStatusService {
  private status: NetworkStatus = {
    isOnline: typeof navigator !== "undefined" ? navigator.onLine : true,
    quality: "good",
    lastCheckedAt: Date.now(),
    rtt: null,
    downlink: null,
  };

  private listeners: Set<NetworkStatusListener> = new Set();
  private pendingRequests: Map<string, PendingRequest> = new Map();
  private checkInterval: ReturnType<typeof setInterval> | null = null;
  private requestIdCounter = 0;
  private isProcessingQueue = false;

  private readonly CHECK_INTERVAL = 30000; // 30 seconds
  private readonly PING_TIMEOUT = 5000; // 5 seconds
  private readonly MAX_PENDING_REQUESTS = 50;
  private readonly DEFAULT_MAX_RETRIES = 3;

  constructor() {
    this.initialize();
  }

  /**
   * Initialize network monitoring
   */
  private initialize(): void {
    if (typeof window === "undefined") return;

    // Listen to browser online/offline events
    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);

    // Use Network Information API if available
    const connection = this.getNetworkConnection();
    if (connection) {
      connection.addEventListener("change", this.handleConnectionChange);
      this.updateFromNetworkInfo(connection);
    }

    // Start periodic connectivity checks
    this.startPeriodicCheck();

    // Initial status check
    this.checkConnectivity();
  }

  /**
   * Get current network status
   */
  getStatus(): Readonly<NetworkStatus> {
    return { ...this.status };
  }

  /**
   * Check if currently online
   */
  isOnline(): boolean {
    return this.status.isOnline;
  }

  /**
   * Get connection quality
   */
  getQuality(): ConnectionQuality {
    return this.status.quality;
  }

  /**
   * Subscribe to status changes
   */
  subscribe(listener: NetworkStatusListener): () => void {
    this.listeners.add(listener);
    // Immediately notify with current status
    listener(this.status);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Queue a request to be executed when online
   */
  async queueRequest<T>(
    executor: () => Promise<T>,
    options: { maxRetries?: number } = {},
  ): Promise<T> {
    // If online, execute immediately
    if (this.status.isOnline) {
      try {
        return await executor();
      } catch (error) {
        // If failed due to network, queue for retry
        if (this.isNetworkError(error)) {
          return this.addToQueue(executor, options);
        }
        throw error;
      }
    }

    // Queue for later execution
    return this.addToQueue(executor, options);
  }

  /**
   * Force a connectivity check
   */
  async checkConnectivity(): Promise<boolean> {
    const startTime = Date.now();

    try {
      // Try to fetch a small resource
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.PING_TIMEOUT);

      await fetch("/health/", {
        method: "HEAD",
        cache: "no-store",
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const rtt = Date.now() - startTime;
      this.updateStatus({
        isOnline: true,
        rtt,
        quality: this.assessQuality(rtt),
        lastCheckedAt: Date.now(),
      });

      // Process pending requests if we're back online
      this.processQueue();

      return true;
    } catch (_error) {
      // Try navigator.onLine as fallback
      const browserOnline = navigator.onLine;

      if (!browserOnline) {
        this.updateStatus({
          isOnline: false,
          quality: "offline",
          rtt: null,
          lastCheckedAt: Date.now(),
        });
      } else {
        // Browser thinks we're online but request failed - might be server issue
        this.updateStatus({
          isOnline: true,
          quality: "poor",
          rtt: null,
          lastCheckedAt: Date.now(),
        });
      }

      return browserOnline;
    }
  }

  /**
   * Get pending request count
   */
  getPendingCount(): number {
    return this.pendingRequests.size;
  }

  /**
   * Clear all pending requests
   */
  clearPendingRequests(): void {
    for (const request of this.pendingRequests.values()) {
      request.reject(new Error("Pending requests cleared"));
    }
    this.pendingRequests.clear();
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    if (typeof window === "undefined") return;

    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("offline", this.handleOffline);

    const connection = this.getNetworkConnection();
    if (connection) {
      connection.removeEventListener("change", this.handleConnectionChange);
    }

    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }

    this.clearPendingRequests();
    this.listeners.clear();
  }

  // ============================================================================
  // Private Methods
  // ============================================================================

  private handleOnline = (): void => {
    logger.info("Network: Browser reports online");
    this.checkConnectivity();
  };

  private handleOffline = (): void => {
    logger.warn("Network: Browser reports offline");
    this.updateStatus({
      isOnline: false,
      quality: "offline",
      rtt: null,
      lastCheckedAt: Date.now(),
    });
  };

  private handleConnectionChange = (): void => {
    const connection = this.getNetworkConnection();
    if (connection) {
      this.updateFromNetworkInfo(connection);
    }
  };

  private getNetworkConnection(): NetworkInformation | null {
    if (typeof navigator === "undefined") return null;

    // NetworkInformation is not in all browsers
    const nav = navigator as Navigator & {
      connection?: NetworkInformation;
      mozConnection?: NetworkInformation;
      webkitConnection?: NetworkInformation;
    };

    return nav.connection || nav.mozConnection || nav.webkitConnection || null;
  }

  private updateFromNetworkInfo(connection: NetworkInformation): void {
    const rtt = connection.rtt ?? null;
    const downlink = connection.downlink ?? null;

    this.updateStatus({
      rtt,
      downlink,
      quality: this.assessQualityFromConnection(connection),
      lastCheckedAt: Date.now(),
    });
  }

  private assessQuality(rtt: number): ConnectionQuality {
    if (rtt < 100) return "excellent";
    if (rtt < 300) return "good";
    return "poor";
  }

  private assessQualityFromConnection(
    connection: NetworkInformation,
  ): ConnectionQuality {
    const effectiveType = connection.effectiveType;

    switch (effectiveType) {
      case "4g":
        return "excellent";
      case "3g":
        return "good";
      case "2g":
      case "slow-2g":
        return "poor";
      default:
        // Fall back to RTT-based assessment
        if (connection.rtt !== undefined) {
          return this.assessQuality(connection.rtt);
        }
        return "good";
    }
  }

  private updateStatus(updates: Partial<NetworkStatus>): void {
    const wasOnline = this.status.isOnline;
    this.status = { ...this.status, ...updates };

    // Notify listeners
    for (const listener of this.listeners) {
      try {
        listener(this.status);
      } catch (error) {
        logger.error("Network status listener error:", error);
      }
    }

    // Log status changes
    if (wasOnline !== this.status.isOnline) {
      logger.info(
        `Network status changed: ${this.status.isOnline ? "online" : "offline"} (quality: ${this.status.quality})`,
      );
    }
  }

  private startPeriodicCheck(): void {
    this.checkInterval = setInterval(() => {
      // Only check if browser thinks we're online but we haven't confirmed recently
      if (navigator.onLine) {
        const timeSinceCheck = Date.now() - this.status.lastCheckedAt;
        if (timeSinceCheck > this.CHECK_INTERVAL) {
          this.checkConnectivity();
        }
      }
    }, this.CHECK_INTERVAL);
  }

  private async addToQueue<T>(
    executor: () => Promise<T>,
    options: { maxRetries?: number },
  ): Promise<T> {
    // Check queue limit
    if (this.pendingRequests.size >= this.MAX_PENDING_REQUESTS) {
      throw new Error("Pending request queue is full");
    }

    const id = `pending_${++this.requestIdCounter}`;

    return new Promise((resolve, reject) => {
      const request: PendingRequest = {
        id,
        executor,
        resolve: resolve as (value: unknown) => void,
        reject,
        createdAt: Date.now(),
        retryCount: 0,
        maxRetries: options.maxRetries ?? this.DEFAULT_MAX_RETRIES,
      };

      this.pendingRequests.set(id, request);
      logger.debug(`Request queued for retry when online: ${id}`);
    });
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessingQueue || !this.status.isOnline) return;
    if (this.pendingRequests.size === 0) return;

    this.isProcessingQueue = true;
    logger.debug(`Processing ${this.pendingRequests.size} pending requests`);

    const requests = Array.from(this.pendingRequests.values());

    for (const request of requests) {
      if (!this.status.isOnline) {
        logger.debug("Went offline during queue processing, stopping");
        break;
      }

      try {
        const result = await request.executor();
        request.resolve(result);
        this.pendingRequests.delete(request.id);
        logger.debug(`Pending request completed: ${request.id}`);
      } catch (error) {
        request.retryCount++;

        if (request.retryCount >= request.maxRetries) {
          request.reject(error);
          this.pendingRequests.delete(request.id);
          logger.warn(
            `Pending request failed after ${request.maxRetries} retries: ${request.id}`,
          );
        } else if (!this.isNetworkError(error)) {
          // Non-network error, don't retry
          request.reject(error);
          this.pendingRequests.delete(request.id);
        }
        // Network error with retries remaining - keep in queue
      }
    }

    this.isProcessingQueue = false;
  }

  private isNetworkError(error: unknown): boolean {
    if (error instanceof Error) {
      const message = error.message.toLowerCase();
      return (
        message.includes("network") ||
        message.includes("failed to fetch") ||
        message.includes("timeout") ||
        message.includes("offline")
      );
    }
    return false;
  }
}

// Type definition for Network Information API
interface NetworkInformation extends EventTarget {
  readonly downlink?: number;
  readonly effectiveType?: "slow-2g" | "2g" | "3g" | "4g";
  readonly rtt?: number;
  readonly saveData?: boolean;
  addEventListener(type: "change", listener: () => void): void;
  removeEventListener(type: "change", listener: () => void): void;
}

// ============================================================================
// Singleton Instance
// ============================================================================

export const networkStatus = new NetworkStatusService();

export { NetworkStatusService };
export type { NetworkStatus };
